<template>
  <div class="app-container">
    <nav class="navbar" :class="{ 'nav-open': isMenuOpen }" :style="navbarStyle">
      <router-link to="/" class="nav-logo">
        <SvgImage height="35" width="150" />
      </router-link>
      <!-- 汉堡菜单按钮（在移动设备上显示） -->
      <button
        class="menu-toggle"
        @click="toggleMenu"
        :class="{ active: isMenuOpen }"
        aria-label="Toggle menu"
      >
        <el-icon class="menu-btn"
          ><Fold v-if="isMenuOpen" /><Expand v-else
        /></el-icon>
      </button>

      <!-- 导航链接容器 -->
        <div class="nav-links" :class="{ 'nav-links-open': isMenuOpen }">
          <router-link to="/" class="nav-link" @click="closeMenu">{{
            t("navbar.home")
          }}</router-link>
        <!-- 产品中心下拉菜单 -->
        <DropdownMenu
        v-show="!isMenuOpen"
          :title="t('navbar.products')"
          :items="products"
          @item-click="handleProductClick"
        />
        <el-collapse v-model="activeNames" @change="handleChange" v-show="isMenuOpen">
          <el-collapse-item :title="t('navbar.products')" name="1">
            <div
          class="dropdown-item2"
          v-for="product in products"
          :key="product.id"
          @click="handleProductClick(product)"
        >
          <div>{{ currentLanguage === 'en' ? (product.productNameEn || product.productName) : product.productName }}</div>
          <el-icon class="dropdown-icon"
            ><ArrowRight v-if="isMenuOpen" /><ArrowDown v-else
          /></el-icon>
        </div>
          </el-collapse-item>
        </el-collapse>

        <!-- <router-link to="/technology" class="nav-link" @click="closeMenu">{{
          t("navbar.technology")
        }}</router-link> -->
        <router-link to="/profile" class="nav-link" @click="closeMenu">{{
          t("navbar.about")
        }}</router-link>
        <!-- <router-link to="/cooperationCases" class="nav-link" @click="closeMenu">{{
          t("about.cases")
        }}</router-link> -->
        <router-link to="/news" class="nav-link" @click="closeMenu">{{
          t("navbar.news")
        }}</router-link>
        <router-link to="/join" class="nav-link" @click="closeMenu">{{
          t("navbar.join")
        }}</router-link>
        <router-link to="/cooperation" class="nav-link" @click="closeMenu">{{
          t("navbar.contact")
        }}</router-link>
        <router-link to="/admin/products" class="nav-link" v-if="isLoggedIn && proxy.$isAdmin" @click="closeMenu">{{
          t("navbar.admin")
        }}</router-link>
        <!-- 登录按钮 -->
        <router-link to="/login" class="nav-link" v-if="!isLoggedIn && proxy.$isAdmin" @click="closeMenu">{{
          t("navbar.login")
        }}</router-link>
        <button class="logout-btn" @click="handleLogout" v-if="isLoggedIn && proxy.$isAdmin">
          {{ t("navbar.logout") }}
        </button>
        <!-- 语言切换按钮 -->
        <button class="language-toggle-btn" @click="toggleLanguage">
          {{ currentLanguage === "zh" ? t("language.en") : t("language.zh") }}
        </button>
      </div>
    </nav>

    <main class="main-content">
      <router-view :key="$route.fullPath" />
    </main>
    <!-- 右上角悬浮窗组件 -->
    <ContactFloat />
    <!-- 底部固定容器 -->
    <Footer :productList="products" />

    <!-- 文本编辑对话框 -->
    <TextEditor
      v-model="currentTextData"
      v-model:visible="editorVisible"
      :content-id="currentContentId"
      @save="handleTextSave"
    />

    <!-- 背景编辑对话框 -->
    <BackgroundEditor
      v-model="currentBackgroundData"
      v-model:visible="backgroundEditorVisible"
      :background-id="currentBackgroundId"
      @save="handleTextSave"
    />

    <!-- 按钮编辑对话框 -->
    <ButtonEditor
      v-model="currentButtonData"
      v-model:visible="buttonEditorVisible"
      :content-id="currentButtonId"
      @save="handleTextSave"
    />
    <el-backtop class="backtop" :right="100" :bottom="100" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, provide, watch, getCurrentInstance, defineAsyncComponent  } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { ElMessage } from "element-plus";
import api from "./api";
import { arrayToObject } from "./utils/utils";
import DropdownMenu from "./components/DropdownMenu.vue";
import Footer from "./components/Footer.vue";
import contentAPI from "./api/modules/content.js";
import contactInfoAPI from "./api/modules/contactInfo.js";
import SvgImage from '@/assets/icon.svg?component'

// 动态导入编辑器组件，减少首屏加载时间
const TextEditor = defineAsyncComponent(() => import("./components/TextEditor.vue"));
const BackgroundEditor = defineAsyncComponent(() => import("./components/BackgroundEditor.vue"));
const ButtonEditor = defineAsyncComponent(() => import("./components/ButtonEditor.vue"));
const { proxy } = getCurrentInstance()
// 响应式数据
const isLoggedIn = ref(false);
const currentLanguage = ref("zh"); // 默认中文
const products = ref([]); // 存储产品列表
const contents = ref([]); // 存储内容数据
const contentMap = ref({}); // 存储转换后的内容对象
const contactInfo = ref([]); // 存储联系信息
const isMenuOpen = ref(false); // 菜单展开状态
const navbarStyle = ref({}); // 导航栏样式
const { t, locale } = useI18n();
// 文本编辑相关数据
const currentTextData = ref(null);
const editorVisible = ref(false);
const editingComponentId = ref(null);
const currentContentId = ref("");

// 背景编辑相关数据
const backgroundEditorVisible = ref(false);
const currentBackgroundId = ref("");

// 按钮编辑相关数据
const currentButtonData = ref({
  zh: "按钮文字",
  en: "Button Text",
  href: "",
  target: "_self",
  bgColor: "#1890ff",
  textColor: "#ffffff",
  size: "medium",
  hidden: false,
});
const buttonEditorVisible = ref(false);
const currentButtonId = ref("");
// 获取路由和i18n实例
const router = useRouter();

// 检查登录状态函数
function checkLoginStatus() {
  // 检查localStorage中是否存在token
  isLoggedIn.value = !!localStorage.getItem("token");
}

// 获取产品数据函数
async function getProducts() {
  try {
    const data = await api.product.getProducts({
      page: 1,
      pageSize: 999,
      is_order: true,
      is_active: 1,
    });
    if (data.success) {
      products.value = data.data.list || [];
      // footerData.value.navColumns[0].items = products.value.map((product) => {
      //   return {
      //     ...product,
      //     name: product.productName,
      //     path: `/product/${product.id}`,
      //   };
      // });

      // 把产品和menu组合起来赋值给footerData.navColumns
      // products.value.map((product) => ({
      //   name: product.name,
      //   path: product.path,
      // }));
    }
  } catch (error) {
    console.error("获取产品列表失败:", error);
  }
}

// 登出处理函数
function handleLogout() {
  // 清除localStorage中的token和用户信息
  localStorage.removeItem("token");
  localStorage.removeItem("user");

  // 更新登录状态
  isLoggedIn.value = false;

  // 重定向到首页
  router.push("/");
}

// 编辑处理函数
function handleEdit() {
  alert(t("messages.editClick"));
}

// 文本编辑处理函数
function handleTextEdit() {
  alert(t("messages.editingText"));
}

// 处理TextCom组件的edit事件
function handleTextComEdit(contentId) {
  console.log("接收到TextCom编辑事件:", contentId);
  currentContentId.value = contentId;
  editorVisible.value = true;
}

// 保存文本编辑
async function handleTextSave(data) {
  console.log("保存文本编辑:", data);

  // 注意：这里传递的第一个参数是content_id
  const response = await contentAPI.updateContent(data);
  const { success, message } = response;
  if (success) {
    // 关闭弹窗
    editorVisible.value = false;
    backgroundEditorVisible.value = false;
    getContents();
    ElMessage({
      message: t("messages.saveSuccess"),
      type: "success",
    });
  } else {
    ElMessage({
      message: message || t("messages.saveFailed"),
      type: "error",
    });
  }
}

// 容器编辑处理函数
function handleContainerEdit() {
  alert(t("messages.editingContainer"));
}

// 处理背景编辑
function handleBackgroundEdit(backgroundId) {
  console.log("接收到背景编辑事件:", backgroundId);
  currentBackgroundId.value = backgroundId;
  backgroundEditorVisible.value = true;
}

// 处理ButtonCom组件的edit事件
function handleButtonComEdit(contentId) {
  console.log("接收到ButtonCom编辑事件:", contentId);
  currentButtonId.value = contentId;
  buttonEditorVisible.value = true;
}

// 保存背景编辑
async function handleBackgroundSave(data) {
  console.log("保存背景编辑:", data);
  // 这里可以添加保存背景数据的逻辑
  // 暂时先关闭弹窗并提示成功
  backgroundEditorVisible.value = false;
  ElMessage({
    message: t("messages.saveSuccess"),
    type: "success",
  });
}

// 语言切换处理函数
function handleLanguageChange(lang) {
  currentLanguage.value = lang;
  // 与i18n实例联动，切换语言
  locale.value = lang;
  console.log("切换语言为:", lang);
  // 简单提示
  ElMessage({
    message: t("messages.languageSwitched", {
      language: t("language." + lang),
    }),
    type: "success",
  });
}

// 切换语言
function toggleLanguage() {
  const newLanguage = currentLanguage.value === "zh" ? "en" : "zh";
  currentLanguage.value = newLanguage;
  locale.value = newLanguage;
  localStorage.setItem("language", newLanguage);
  ElMessage({
    message: t("messages.languageSwitched", {
      language: t("language." + newLanguage),
    }),
    type: "success",
  });
}

// 切换菜单显示/隐藏
function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
  // 阻止事件冒泡
  event.stopPropagation();
}

// 关闭菜单
function closeMenu() {
  isMenuOpen.value = false;
}

// 跳转到产品详情页
function goToProductDetail(productId) {
  router.push(`/product/${productId}`);
}

// 处理自定义下拉菜单产品点击
const handleProductClick = (product) => {
  goToProductDetail(product.id);
  isMenuOpen.value = false;
};

// 跳转到产品中心首页
function goToProductsHome() {
  router.push("/products");
}
function getValue(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}
// 获取内容数据
async function getContents() {
  try {
    // 调用内容接口获取全部内容数据
    const data = await api.content.getContents({
      page: 1,
      limit: 99999, // 获取所有内容
    });

    if (data.success && data.data && data.data.list) {
      contents.value = data.data.list;
      // 使用arrayToObject方法将数组转换为对象，contentId为key，contentText为value
      contentMap.value = arrayToObject(
        contents.value,
        "contentId",
        "contentText",
        getValue
      );
      // console.log("内容数据转换完成:", contentMap.value);
    }
  } catch (error) {
    console.error("获取内容数据失败:", error);
  }
}

// 获取联系信息
async function getContactInfo() {
  try {
    // 调用联系信息接口获取所有联系信息
    const response = await contactInfoAPI.getContactInfoList({
      page: 1,
      limit: 99999, // 获取所有联系信息
    });

    if (response.code === 200) {
      contactInfo.value = response.data || [];
      console.log("联系信息获取完成:", contactInfo.value);
    } else {
      console.error("获取联系信息失败:", response.msg);
    }
  } catch (error) {
    console.error("获取联系信息失败:", error);
  }
}
//如果是管理页面，把navbar改为relative
watch(
  () => router.currentRoute.value,
  (newRoute) => {
    const newPath = newRoute.path;
    // 修改navbar样式
    if (newPath.startsWith("/admin")) {
      document.querySelector(".navbar").style.position = "relative";
      document.querySelector(".navbar").style.backgroundColor = "#222";
      // navbarPosition.value = "relative";
    } else {
      // if (newPath === "/") {
      //   document.querySelector(".navbar").style.position = "absolute";
      //   document.querySelector(".navbar").style.backgroundColor = "transparent";
      //   document.querySelector(".navbar").style.top = "24px";
      //   // navbarPosition.value = "absolute";
      // } else {
      //   document.querySelector(".navbar").style.position = "absolute";
      //   document.querySelector(".navbar").style.backgroundColor = "transparent";
      //   document.querySelector(".navbar").style.top = "0";
      //   // navbarPosition.value = "absolute";
      // }
      document.querySelector(".navbar").style.position = "absolute";
      document.querySelector(".navbar").style.backgroundColor = "transparent";
      document.querySelector(".navbar").style.top = "0";
    }
  }
);
watch(
  () => router.currentRoute.value,
  (newRoute) => {
    // 根据当前路由修改页面名称
    const routeTitleMap = {
      'Home': t('navbar.home'),
      'Technology': t('navbar.technology'),
      'Profile': t('navbar.about'),
      'Login': t('login.title'),
      'ProductPage': t('product.title'),
      'News': t('news.title'),
      'Jobs': t('jobs.title'),
      'Cooperation': t('cooperation.title'),
      'ProductManagement': t('admin.products.title'),
      'NewsManagement': t('admin.news.title'),
      'JobManagement': t('admin.jobs.title'),
      'JobApplicationManagement': t('admin.jobApplications.title'),
      'CooperationManagement': t('admin.cooperation.title'),
      'BannerManagement': t('admin.banner.title')
    };
    
    // 设置页面标题
    const pageTitle = routeTitleMap[newRoute.name] || t('navbar.home');
    // document.title = `${pageTitle} - KUN BOT`;
  },
  { immediate: true }
);

// 修改navbar文字颜色的方法，支持修改默认颜色和hover颜色
const changeNavbarTextColor = (color, hoverColor = null) => {
  // 使用双保险方案：同时设置style绑定和CSS变量
  navbarStyle.value.color = color;
  
  // 获取navbar元素
  const navbarElement = document.querySelector('.navbar');
  
  // 设置默认文字颜色
  if (navbarElement) {
    navbarElement.style.setProperty('--navbar-text-color', color);
  }
  document.documentElement.style.setProperty('--navbar-text-color', color);
  
  // 如果提供了hover颜色，则同时设置hover颜色
  if (hoverColor) {
    if (navbarElement) {
      navbarElement.style.setProperty('--navbar-text-hover-color', hoverColor);
    }
    document.documentElement.style.setProperty('--navbar-text-hover-color', hoverColor);
  }
};

// 提供contentMap给子组件
provide("contentMap", contentMap);
provide("isLoggedIn", isLoggedIn);
provide("currentLanguage", currentLanguage);
provide("handleTextComEdit", handleTextComEdit);
provide("handleButtonComEdit", handleButtonComEdit);
provide("openBackgroundEditor", (backgroundId) => {
  handleBackgroundEdit(backgroundId);
});
// 提供修改navbar文字颜色的方法给子组件
provide("changeNavbarTextColor", changeNavbarTextColor);
// 提供products给子组件
provide("products", products);
// 提供更新产品数据的方法
provide("updateProducts", getProducts);
provide("updatecontentMap", getContents);
// 提供联系信息给子组件
provide("contactInfo", contactInfo);
// 提供更新联系信息的方法
provide("updateContactInfo", getContactInfo);

// 生命周期钩子
onMounted(() => {
  // 初始化时检查登录状态
  checkLoginStatus();

  // 并行获取产品数据、内容数据和联系信息，减少首屏加载时间
  Promise.all([
    getProducts(),
    getContents(),
    getContactInfo()
  ]);

  // 监听路由变化，检查登录状态
  router.beforeEach((to, from, next) => {
    checkLoginStatus();
    next();
  });
});
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  line-height: 1.6;
  color: #333;
  background-color: #f5f5f5;
  overflow-x: hidden; /* 防止水平滚动 */
  overflow-y: auto;
}

.app-container {
  /* min-height: 100vh; */
  /* display: flex;
  flex-direction: column; */
  width: 100%;
  overflow-x: hidden;
  /* height: 100vh;
  overflow-y: auto; */
}

.navbar {
  /* background-color: #222222; */
  position: absolute;
  top: 0;
  left: 0;
  color: var(--navbar-text-color, white);
  width: 100%;
  padding: 1rem 4.17vw; /* 减小左右内边距 */
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  z-index: 1000;
  height: 70px;
  /* 移除局部CSS变量定义，使用全局定义的变量 */
}

.nav-links {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

/* 汉堡菜单按钮基础样式 */
.menu-toggle {
  display: none; /* 默认隐藏 */
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 21px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1100;
}

.menu-icon {
  display: block;
  width: 100%;
  height: 3px;
  background-color: white;
  border-radius: 3px;
  transition: all 0.3s ease;
}
.el-collapse,
.el-collapse-item__wrap,
.el-collapse-item__header {
  --el-fill-color-blank: transparent;
  border: none !important;
  width: 100%;
  padding: 0px !important;
  /* height: auto !important; */
  --el-collapse-header-height: auto
}
.el-collapse-item__content{
  padding: 0px !important;
}
.el-collapse-item__arrow{
 display: none !important;
}


/* 响应式设计 - 小屏幕设备 */
@media (max-width: 1024px) {
  /* 显示汉堡菜单按钮 */
  .menu-toggle {
    display: block;
  }

  /* 隐藏并调整导航链接为垂直下拉菜单 */
  .nav-links {
      position: fixed;
      top: 0;
      right: 0;
      opacity: 0;
      height: 100vh;
      width: 280px;
      background-color: #004a99;
      flex-direction: column;
      align-items: flex-start;
      padding: 60px 20px 20px;
      transition: opacity 0.5s ease;
      box-shadow: -5px 0 15px rgba(0, 0, 0, 0.2);
      z-index: 999;
      pointer-events: none; /* 隐藏时不捕获点击事件 */
    }

  /* 菜单展开时的样式 */
.nav-open {
      position: fixed !important;
      top:0;
      right: 0;
    }
    .nav-links.nav-links-open {
      opacity: 1;
      pointer-events: auto; /* 展开时恢复点击事件捕获 */
    }
  .menu-btn {
    font-size: 24px !important;
    color: white !important;
  }
  /* 汉堡菜单按钮的动画效果 */
  /* .menu-toggle.active .menu-icon:nth-child(1) {
    transform: rotate(45deg) ;
  }
  
  .menu-toggle.active .menu-icon:nth-child(2) {
    opacity: 0;
  }
  
  .menu-toggle.active .menu-icon:nth-child(3) {
    transform: rotate(-45deg);
  } */
}

/* 在更小的屏幕上进一步优化 */
@media (max-width: 768px) {
  .nav-links {
    width: 100%;
    /* right: -100%; */
  }

  .navbar {
    padding: 1rem;
  }
}

.main-content {
  /* flex: 1; */
  /* padding: 1rem; 使用较小的内边距 */
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  /* overflow-x: hidden;
  height: auto; */
  /* height: 100vh; */
  /* overflow-y: auto; */
}

.nav-logo {
  color: inherit;
  
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: bold;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.nav-logo :hover{
    color: #006DFF
}

.nav-logo-image {
  height:35px;
}

.nav-links {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.debug-info {
  color: yellow;
  font-size: 12px;
  margin-right: 10px;
}

.nav-link {
  color: inherit;
  text-decoration: none;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  position: relative;
  transition: color 0.3s;
  display: inline-block;
  cursor: pointer;
  font-weight: 500;
}

.nav-dropdown {
  display: flex;
  align-items: center;
  height: 100%;
}

/* 确保下拉菜单的样式与导航栏一致 */
:deep(.el-dropdown-menu__item) {
  font-size: 0.9rem;
  padding: 8px 20px;
}

/* 确保下拉菜单项的悬停效果 */
:deep(.el-dropdown-menu__item:hover) {
  background-color: #f0f0f0;
  color: #222;
}

.nav-link::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background-color: var(--navbar-text-color, white);
  transition: width 0.3s ease-in-out;
}

.nav-link:hover {
  color: var(--navbar-text-hover-color, #fff);
}

.nav-link:hover::after {
  width: calc(100% - 2rem);
  background-color: var(--navbar-text-hover-color, white);
}

/* 激活状态的导航链接 */
.router-link-active::after,
.router-link-exact-active::after {
  width: calc(100% - 2rem);
}

/* 确保下拉菜单项的悬停效果 */
:deep(.el-dropdown-menu__item:hover) {
  background-color: #f0f0f0;
  color: var(--navbar-text-hover-color, #222);
}

.logout-btn {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.logout-btn:hover {
  background-color: #d32f2f;
}

/* 语言切换按钮样式 */
.language-toggle-btn {
  background: none;
  border: none;
  color: var(--navbar-text-color, white);
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
  transition: color 0.3s, background-color 0.3s;
  font-weight: 500;
}
.el-collapse-item__title{
    color: var(--navbar-text-color, white);
  text-decoration: none;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  position: relative;
  transition: color 0.3s;
  display: inline-block;
  cursor: pointer;
  font-weight: 500;
  text-align: center !important;
}
.dropdown-item2 {
  color: var(--navbar-text-color, white);
  font-weight: 400;
  /* font-size: 0.8rem; */
  line-height: 24px;
  margin-bottom: 12px;
  /* width: 100%; */
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 24px 12px;
}

/* .language-toggle-btn:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.1);
} */

/* 底部容器样式 - 位于所有内容下方 */

/* HoverMask示例样式 */
.demo-section {
  margin-top: 40px;
  padding: 20px;
  border: 1px solid #eaeaea;
  border-radius: 8px;
}
.nav-dropdown-link {
  display: inline-block;
  position: relative;
  color: inherit;
  text-align: center;
}
.demo-item {
  margin-bottom: 30px;
}

.demo-content {
  width: 300px;
  height: 150px;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.backtop{
  --el-color-primary: #ccc;
}
</style>
